/**
 * Task categories — the fixed set of six business areas a task can be
 * classified under, used by reporting + project board filtering.
 *
 * The slugs are the on-the-wire / in-DB values. The labels are the
 * human-readable display strings used in selects, badges, and reports.
 * The token name maps to a semantic CSS variable defined in
 * `packages/web/src/design/tokens.ts` (and mirrored in `globals.css`)
 * so the badge colours stay theme-aware and pass through any per-tenant
 * brand override.
 *
 * Adding / removing a slug requires:
 *   1. Updating this array (single source of truth).
 *   2. Adding the matching `--color-category-<slug>-*` vars to tokens.ts
 *      + globals.css so the badge renders.
 *   3. Picking sensible defaults for existing rows (the DB column is
 *      NOT NULL with `'product_development'` as the historical backfill).
 */
export const TASK_CATEGORY_SLUGS = [
    "governance",
    "operations",
    "product_development",
    "sales_and_marketing",
    "client_delivery",
    "finance",
];
/** Human-readable label for each category slug. */
export const TASK_CATEGORY_LABELS = {
    governance: "Governance",
    operations: "Operations",
    product_development: "Product development",
    sales_and_marketing: "Sales and marketing",
    client_delivery: "Client delivery",
    finance: "Finance",
};
/**
 * Default category for backfilling pre-existing tasks at migration
 * time + for any code path that needs a fall-back ("the user picked
 * nothing" should never reach this — the Zod schema is required —
 * but the DB-level migration uses it).
 */
export const TASK_CATEGORY_DEFAULT = "product_development";
/**
 * Map a category slug to the matching semantic token suffix. The badge
 * component composes the full Tailwind utility (`bg-category-${tone}`,
 * `text-category-${tone}-fg`). Keeping the suffix here (rather than the
 * raw Tailwind class) lets us swap the underlying token without
 * touching consumers.
 */
export const TASK_CATEGORY_TOKENS = {
    governance: "governance",
    operations: "operations",
    product_development: "product",
    sales_and_marketing: "sales",
    client_delivery: "client",
    finance: "finance",
};
/**
 * Type guard used by route handlers + URL query parsing to confirm a
 * caller-supplied string is one of the supported slugs.
 */
export function isTaskCategory(value) {
    return typeof value === "string" && TASK_CATEGORY_SLUGS.includes(value);
}
//# sourceMappingURL=task-categories.js.map